<template>
  <section class="ins-prov">
    <h2 class="ins-chart-title">{{ heading }}</h2>
    <p class="ins-prov-intro"><slot name="intro">Every figure on this report is pulled in automatically — nothing is typed by hand. Here's where each number comes from and what it measures.</slot></p>

    <div v-if="sources && sources.length" class="ins-src-cards">
      <div v-for="(s, i) in sources" :key="'src' + i" class="ins-src-card">
        <div class="ins-src-ic" aria-hidden="true">{{ s.icon }}</div>
        <div class="ins-src-body">
          <div class="ins-src-name">{{ s.name }}</div>
          <div class="ins-src-desc">{{ s.desc }}</div>
          <a v-if="s.url" :href="s.url" target="_blank" rel="noopener" class="ins-src-link">Open ↗</a>
          <div v-if="s.feeds" class="ins-src-feeds">{{ s.feeds }}</div>
        </div>
      </div>
    </div>

    <table v-if="provenance && provenance.metrics && provenance.metrics.length" class="ins-prov-tbl">
      <thead><tr><th>Metric</th><th>Where it comes from</th><th>What it measures</th></tr></thead>
      <tbody>
        <tr v-for="m in provenance.metrics" :key="m.key">
          <td class="ins-prov-metric"><b>{{ m.tab || m.label }}</b></td>
          <td>
            <a v-if="m.source_url" :href="m.source_url" target="_blank" rel="noopener" class="ins-prov-obj">{{ m.source_object || m.source_system }} ↗</a>
            <span v-else class="ins-prov-obj">{{ m.source_object || m.source_system }}</span>
            <div v-if="m.source_detail || m.source_file" class="ins-src-file">{{ m.source_detail || m.source_file }}</div>
          </td>
          <td class="ins-prov-desc">{{ m.description }}</td>
        </tr>
      </tbody>
    </table>
    <p v-else class="ins-modal-empty">Source details aren't available right now.</p>

    <p v-if="freshness" class="ins-prov-foot">Refreshes automatically overnight<template v-if="freshness.data_through"> — data through {{ fmtDate(freshness.data_through) }}</template><template v-if="freshness.next_refresh_label">; next update {{ freshness.next_refresh_label }}</template>.</p>
    <p class="ins-prov-help">Think a number looks off? Email <a :href="'mailto:' + helpEmail">{{ helpEmail }}</a> with the metric name and the date it's showing, and we'll trace it back to the source for you.</p>
  </section>
</template>

<script>
/* Shared "Data Sources" / provenance section. `sources` = source-system cards the report supplies
 * ([{ icon, name, desc, feeds?, url? }]); `provenance` = the /provenance response ({ metrics:[...] }).
 * Metric rows tolerate both the WOI shape (tab/source_object/source_detail) and the FI/MI shape
 * (label/source_system/source_file/source_url). */
import { fmtDate } from "./insightsFormat";
export default {
  name: "InsProvenance",
  props: {
    provenance: { type: Object, default: null },
    sources: { type: Array, default: () => [] },
    freshness: { type: Object, default: null },
    heading: { type: String, default: "Where this report's numbers come from" },
    helpEmail: { type: String, default: "helpdesk@jcsteele.com" },
  },
  methods: { fmtDate },
};
</script>

<style scoped>
.ins-prov { max-width: 760px; margin: 0 auto; }
.ins-prov-intro { font-size: 12px; color: var(--ins-text-sub); margin: 0 0 12px; }
.ins-src-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 14px; margin-bottom: 18px; }
.ins-src-card { display: flex; gap: 12px; border: 1px solid var(--ins-border); border-radius: 10px; padding: 12px 14px; background: var(--ins-card-alt); }
.ins-src-ic { font-size: 22px; flex: 0 0 auto; line-height: 1.2; }
.ins-src-name { font-size: 14px; font-weight: 700; color: var(--ins-text); }
.ins-src-desc { font-size: 12px; color: var(--ins-text-sub); margin-top: 3px; }
.ins-src-link { font-size: 12px; color: var(--ins-accent); font-weight: 600; display: inline-block; margin-top: 4px; }
.ins-src-feeds { font-size: 11px; color: var(--ins-text-mute); margin-top: 6px; font-weight: 600; }
.ins-prov-tbl { width: 100%; border-collapse: collapse; font-size: 12px; }
.ins-prov-tbl th, .ins-prov-tbl td { text-align: left; padding: 7px 8px; border-bottom: 1px solid var(--ins-border-soft); color: var(--ins-text-dim); vertical-align: top; }
.ins-prov-tbl th { color: var(--ins-label); font-weight: 700; text-transform: uppercase; font-size: 11px; }
.ins-prov-metric { white-space: nowrap; color: var(--ins-text); }
.ins-prov-obj { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 11px; color: var(--ins-text-dim); }
.ins-src-file { font-size: 11px; color: var(--ins-text-mute); margin-top: 3px; }
.ins-prov-desc { color: var(--ins-text-sub); }
.ins-prov-foot { font-size: 11px; color: var(--ins-text-mute); margin: 10px 2px 0; }
.ins-prov-help { font-size: 12px; color: var(--ins-text-sub); margin: 12px 2px 0; padding-top: 10px; border-top: 1px solid var(--ins-border-soft); }
.ins-prov-help a { color: var(--ins-accent); font-weight: 600; }
.ins-modal-empty { color: var(--ins-text-mute); font-size: 14px; padding: 20px 0; text-align: center; }
</style>
